import {memo, useMemo, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import type {
  CanvasData,
  CanvasBrandColors,
  CanvasActionHandlers,
} from './types';
import {CanvasHero} from './canvas-hero';
import {
  CanvasFooter,
  type CanvasFooterAdvisor,
  type CanvasFooterCompliance,
} from './canvas-footer';
import {buildCanvasBrandAttributes, buildCanvasCssVars} from './utils';
import {BLOCK_RENDERER_MAP} from './block-renderers';

interface CanvasRendererProps {
  canvas: CanvasData;
  brand?: CanvasBrandColors;
  actions?: CanvasActionHandlers;
  advisor: CanvasFooterAdvisor | null;
  compliance: CanvasFooterCompliance | null;
  className?: string;
  afterBlocks?: ReactNode;
}

export const CanvasRenderer = memo(function CanvasRenderer({
  canvas,
  brand,
  actions,
  advisor,
  compliance,
  className,
  afterBlocks,
}: CanvasRendererProps) {
  const visibleBlocks = useMemo(
    () =>
      [...canvas.blocks]
        .sort((a, b) => a.position - b.position)
        .filter((block) => {
          const config = BLOCK_RENDERER_MAP[block.type];
          return config ? !config.isEmpty(block) : false;
        }),
    [canvas.blocks],
  );

  const cssVars = useMemo(() => buildCanvasCssVars(brand), [brand]);
  const brandAttributes = useMemo(
    () => buildCanvasBrandAttributes(brand),
    [brand],
  );

  return (
    <div className={cn(className)} style={cssVars} {...brandAttributes}>
      <CanvasHero canvas={canvas} />

      <div>
        {visibleBlocks.map((block) => {
          const config = BLOCK_RENDERER_MAP[block.type];
          const Component = config.component;
          return <Component key={block.id} block={block} actions={actions} />;
        })}
      </div>

      {afterBlocks}

      <CanvasFooter advisor={advisor} compliance={compliance} />
    </div>
  );
});
CanvasRenderer.displayName = 'CanvasRenderer';
